import { Button } from "primereact/button";
import FlagIcon from "@/assets/icons/limite-flag-header.svg?react";
import { Dialog } from "@/ds";
import type { RelacaoLimiteObra } from "../../hooks/nova-obra.condicoes";
import { LIMITES_DIALOG } from "../../nova-obra.constants";
import { formatDataBr } from "../format-data";
import styles from "./limites-classe-dialog.module.scss";
import type {
  LimitesClasseDialogProps,
  LimitesClasseDialogViewModel,
} from "./types-limites-classe-dialog";

/** Classe do chip "Em relação à obra": o módulo usa `camelCaseOnly`, `styles["VENCIDO"]` seria `undefined`. */
const CLASSE_RELACAO = {
  VENCIDO: "vencido",
  ANTES_DO_INICIO: "antesDoInicio",
  DURANTE_A_OBRA: "duranteAObra",
  APOS_A_OBRA: "aposAObra",
} as const satisfies Record<RelacaoLimiteObra, string>;

/** Modal de consulta "Ver todos" dos limites de classe (HU 2549, Figma `10229:689104`), só leitura. */
export function LimitesClasseDialogView(
  props: LimitesClasseDialogProps & LimitesClasseDialogViewModel,
) {
  const { limites, embarcacaoNome, hoje, periodo, onClose, linhas } = props;

  return (
    <Dialog
      visible
      modal
      onHide={onClose}
      size={periodo ? "xl" : "lg"}
      className={`${styles.dialog} ${periodo ? styles.comObra : styles.semObra}`}
      headerClassName={styles.header}
      contentClassName={styles.content}
      pt={{ footer: { className: styles.footerRoot } }}
      data-testid="limites-classe-dialog"
      aria-label={LIMITES_DIALOG.title}
      header={
        <div className={styles.headerContent}>
          <span className={styles.badge} aria-hidden>
            <FlagIcon />
          </span>
          <div className={styles.headerText}>
            <h2 className={styles.title}>{LIMITES_DIALOG.title}</h2>
            <p className={styles.subtitle}>{embarcacaoNome}</p>
          </div>
        </div>
      }
      footer={
        <div className={styles.footer}>
          <Button type="button" label={LIMITES_DIALOG.fechar} onClick={onClose} />
        </div>
      }
    >
      <div className={styles.body}>
        {limites.status === "loading" && <p className={styles.nota}>{LIMITES_DIALOG.carregando}</p>}
        {(limites.status === "error" || limites.status === "forbidden") && (
          <p className={styles.erro} role="alert">
            {limites.errorMessage}
          </p>
        )}
        {limites.status === "success" && limites.data.length === 0 && (
          <p className={styles.nota}>{LIMITES_DIALOG.vazio}</p>
        )}

        {limites.status === "success" && limites.data.length > 0 && (
          <>
            <div className={styles.tabelaWrapper}>
              <table className={`${styles.tabela} ${periodo ? "" : styles.tabelaSemObra}`}>
                <thead>
                  <tr>
                    <th>{LIMITES_DIALOG.vistoria}</th>
                    <th>{LIMITES_DIALOG.dataLimite}</th>
                    <th>{LIMITES_DIALOG.vencimento}</th>
                    {periodo && <th>{LIMITES_DIALOG.emRelacaoObra}</th>}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((linha) => (
                    <tr key={linha.chave} className={linha.critico ? styles.critico : undefined}>
                      <th scope="row" className={styles.vistoria}>
                        {linha.descricao}
                      </th>
                      <td
                        className={[
                          styles.data,
                          linha.critico && styles.dataCritica,
                          !periodo && styles.dataSemInicio,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {formatDataBr(linha.dataLimite)}
                      </td>
                      <td
                        data-destaque={linha.destaque}
                        className={linha.destaque ? styles.vencimentoCritico : styles.vencimento}
                      >
                        {linha.vencimento}
                      </td>
                      {linha.relacao && (
                        <td>
                          <span
                            className={`${styles.tag} ${styles[CLASSE_RELACAO[linha.relacao.relacao]]}`}
                          >
                            {linha.relacao.texto}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={styles.nota}>
              {LIMITES_DIALOG.nota} ({formatDataBr(hoje)}). {LIMITES_DIALOG.fonte}
            </p>
          </>
        )}
      </div>
    </Dialog>
  );
}
